/**
 * Tasks Page
 * View and manage pickup and RTO tasks
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import {
  TaskTable,
  TaskMap,
} from '../components/tasks';
import { useTasks } from '../hooks/useApi';
import { usePolling } from '../hooks/usePolling';
import { getZones } from '../services/zones.api';
import { markTaskAsDone } from '../services/tasks.api';
import { getTasks } from '../services/tasks.api';
import { releaseTaskFromRoute } from '../services/routes.api';
import { getSlotConfig } from '../services/pickupSlots.api';
import { Route, List, Map, AlertCircle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import type { RoutingTask } from '../types';
import { getBaghdadToday } from '../utils/date';
import { cn } from '../utils/cn';
import { matchesSearch } from '../utils/search';
import { useCan } from '../contexts/useAuth';

// The page's filters that have a badge or tab count. See applyFilters.
type FilterKey = 'planned' | 'dedicated' | 'type' | 'blocked' | 'cold';

export default function Tasks() {
  const can = useCan();
  const navigate = useNavigate();
  
  // View mode: list or map
  const [viewMode, setViewMode] = useState<'list' | 'map' | 'split'>('split');

  // Selected tasks for routing
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());

  // Search query
  const [searchQuery, setSearchQuery] = useState('');

  /*
    Date filter mode. This filter runs server-side on `t.created_at`, which never
    moves forward — so a pickup booked on Friday and still uncollected on Tuesday
    drops out of the "today" view while remaining open. Coming back from the
    31/7-3/8 holiday, "today" showed 23 of 292 open tasks; the other 269 (5,263
    orders) were the backlog everyone was looking for.
  */
  const [dateMode, setDateMode] = useState<'today' | 'last_two_days' | 'last_7_days' | 'specific_date'>('today');

  // Specific date for the 'specific_date' mode (YYYY-MM-DD format)
  const [specificDate] = useState<string>(getBaghdadToday());

  // Compute the dates to fetch based on mode
  const dateFilter = useMemo(() => {
    const todayStr = getBaghdadToday();
    if (dateMode === 'today') {
      return todayStr;
    }
    if (dateMode === 'specific_date') {
      return specificDate;
    }
    if (dateMode === 'last_7_days') {
      // Sent as one from/to query, not seven day-by-day requests.
      const from = new Date();
      from.setDate(from.getDate() - 6);
      return {
        from: from.toLocaleDateString('en-CA', { timeZone: 'Asia/Baghdad' }),
        to: todayStr,
      };
    }
    // Last two days: today and yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return [yesterday.toLocaleDateString('en-CA', { timeZone: 'Asia/Baghdad' }), todayStr];
  }, [dateMode, specificDate]);

  // Status filter - multi-select: array of status slugs, default to ['new', 'assigned']
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const saved = localStorage.getItem('tasksStatusFilter');
    return saved ? JSON.parse(saved) : ['new', 'assigned'];
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Zone filter - multi-select: array of zone names, empty means all
  const [zoneFilter, setZoneFilter] = useState<string[]>(() => {
    const saved = localStorage.getItem('tasksZoneFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [zoneDropdownOpen, setZoneDropdownOpen] = useState(false);
  const zoneDropdownRef = useRef<HTMLDivElement>(null);

  // Hide planned tasks filter - default to true (hide planned tasks)
  const [hidePlanned, setHidePlanned] = useState(() => {
    const saved = localStorage.getItem('tasksHidePlanned');
    return saved ? JSON.parse(saved) : true;
  });

  // Narrow to tasks an officer pulled off a route today (held out of auto-routing).
  const [removedOnly, setRemovedOnly] = useState<boolean>(() => {
    const saved = localStorage.getItem('tasksRemovedOnly');
    return saved ? JSON.parse(saved) : false;
  });

  // The fleet manager's action list: tasks automation will never place. Everything
  // else on this page either resolves itself or is held on purpose.
  const [blockedOnly, setBlockedOnly] = useState<boolean>(() => {
    const saved = localStorage.getItem('tasksBlockedOnly');
    return saved ? JSON.parse(saved) : false;
  });

  /**
   * Cold-chain only. `cooling_count` already arrives on every task and the
   * snowflake badge already renders it — this just narrows to them, which is
   * what makes the daily "how much cold is there, and where" question
   * answerable without a database query.
   */
  const [coldOnly, setColdOnly] = useState<boolean>(() => {
    const saved = localStorage.getItem('tasksColdOnly');
    return saved ? JSON.parse(saved) : false;
  });

  // Dedicated driver filter - show only dedicated or hide dedicated tasks
  const [dedicatedFilter, setDedicatedFilter] = useState<'all' | 'dedicated' | 'non_dedicated'>(() => {
    const saved = localStorage.getItem('tasksDedicatedFilter');
    return saved ? saved as 'all' | 'dedicated' | 'non_dedicated' : 'all';
  });

  // Task type filter - show all, pickups only, or RTOs only
  const [taskTypeFilter, setTaskTypeFilter] = useState<'all' | 'pickup' | 'rto' | 'cash'>(() => {
    const saved = localStorage.getItem('tasksTypeFilter');
    return saved ? saved as 'all' | 'pickup' | 'rto' | 'cash' : 'all';
  });

  /**
   * Pickup-slot filter — 'all', a slot code (S1..S5), 'none' (no slot), or a
   * SHIFT code. One filter rather than a separate shift control: a shift IS a
   * group of slots, so two competing selects would let an officer pick SHIFT1
   * and S4 at once and see an empty table with no explanation.
   */
  const [slotFilter, setSlotFilter] = useState<string>(() => {
    return localStorage.getItem('tasksSlotFilter') || 'all';
  });

  // Shift grouping comes from fleet config; empty when shift routing is off, in
  // which case no shift chips render at all. Never hardcode the map.
  const { data: slotConfig } = useQuery({
    queryKey: ['slotConfig'],
    queryFn: getSlotConfig,
    staleTime: 10 * 60_000,
    // The pickup-slots surface 503s when the feature is disabled — that is a
    // legitimate state, not an error worth retrying or surfacing here.
    retry: false,
  });
  const shifts = slotConfig?.shifts ?? [];
  // A plain record, not a Map — `Map` in this file is the lucide-react icon.
  const slotsByShift = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const s of shifts) out[s.code] = s.slot_codes;
    return out;
  }, [shifts]);

  // The filter is remembered in localStorage, so a shift code can outlive shift
  // mode being turned off (or a shift being renamed in SHIFT_SLOT_MAP). Left
  // alone it would fall through to the exact-slot test, match nothing, and show
  // an empty table with no reason given. Only reset once config has actually
  // loaded — before that `shifts` is legitimately empty.
  useEffect(() => {
    if (!slotConfig) return;
    if (slotFilter.startsWith('SHIFT') && !slotsByShift[slotFilter]) {
      setSlotFilter('all');
      localStorage.setItem('tasksSlotFilter', 'all');
    }
  }, [slotConfig, slotFilter, slotsByShift]);

  // Toast notifications
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Mark as done state
  const [isMarkingDone, setIsMarkingDone] = useState<string | null>(null);

  // Unassign (release task back to pool) state
  const [isUnassigning, setIsUnassigning] = useState<string | null>(null);

  // Scroll position ref for maintaining scroll after refresh
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Polling state
  const [isPolling, setIsPolling] = useState(false);
  const [lastPolledAt, setLastPolledAt] = useState<Date | null>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
      if (zoneDropdownRef.current && !zoneDropdownRef.current.contains(event.target as Node)) {
        setZoneDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch tasks
  const {
    data: taskData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useTasks(dateFilter || undefined, statusFilter);

  // Polling function for auto-refresh
  const pollingFn = useCallback(async () => {
    // Store scroll position before refresh
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }
    
    const result = await getTasks(dateFilter || undefined, statusFilter);
    return result;
  }, [dateFilter, statusFilter]);

  // Handle data change from polling
  const handlePollingDataChange = useCallback(() => {
    setIsPolling(true);
    setLastPolledAt(new Date());
    
    // Refetch to update React Query cache
    refetch().then(() => {
      // Restore scroll position after refresh
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
        setIsPolling(false);
      }, 100);
    });
  }, [refetch]);

  // Setup polling - 30 second interval with exponential backoff on errors
  usePolling(pollingFn, 30000, {
    enabled: !isLoading && !isFetching && !isMarkingDone && !isUnassigning,
    immediate: false, // Don't poll immediately, let useTasks handle initial fetch
    maxInterval: 120000, // Max 2 minutes between polls on errors
    onDataChange: handlePollingDataChange,
    onError: (error) => {
      console.error('Polling error:', error);
    },
  });

  // Fetch zones from API
  const [availableZones, setAvailableZones] = useState<string[]>([]);
  useEffect(() => {
    async function fetchZones() {
      try {
        const zones = await getZones(1); // cityId = 1 (Baghdad)
        setAvailableZones(zones.map(z => z.name).sort());
      } catch (error) {
        console.error('Failed to fetch zones:', error);
        // Fallback to zones from tasks
        if (taskData?.tasks) {
          const zoneNames = new Set<string>();
          taskData.tasks.forEach((task) => {
            if (task.zone_name) {
              zoneNames.add(task.zone_name);
            }
          });
          setAvailableZones(Array.from(zoneNames).sort());
        }
      }
    }
    fetchZones();
  }, [taskData?.tasks]);

  // Filter tasks by every filter on the page (see FilterKey). `skip` drops one filter.
  const applyFilters = useCallback((tasks: RoutingTask[], skip?: FilterKey) => {
    let filtered = tasks;

    /*
      "Hide planned" defaults ON, so anything is_planned disappears from the page
      entirely. After the 31/7-3/8 holiday that hid 160 tasks / 2,026 orders sitting
      on driverless DRAFT routes — work nothing would ever collect, filtered out of
      the one view that would have shown it, for six days.

      A task whose route has no driver is not planned in any useful sense, so the
      filter must not treat it as such. `planned_not_dispatched` is the backend's
      verdict for exactly that case; keep those visible.
    */
    if (hidePlanned && skip !== 'planned') {
      filtered = filtered.filter(
        (task) => !task.is_planned || task.routing_status?.code === 'planned_not_dispatched'
      );
    }

    // Apply dedicated driver filter
    if (skip !== 'dedicated') {
      if (dedicatedFilter === 'dedicated') {
        filtered = filtered.filter((task) => task.requires_dedicated_driver);
      } else if (dedicatedFilter === 'non_dedicated') {
        filtered = filtered.filter((task) => !task.requires_dedicated_driver);
      }
    }

    // Apply task type filter (pickup/rto/cash)
    if (skip !== 'type') {
      if (taskTypeFilter === 'pickup') {
        filtered = filtered.filter((task) => task.item_type === 'pickup');
      } else if (taskTypeFilter === 'rto') {
        filtered = filtered.filter((task) => task.item_type === 'rto');
      } else if (taskTypeFilter === 'cash') {
        filtered = filtered.filter((task) => task.item_type === 'cash');
      }
    }

    // Officer-removed tasks only. Deliberately AFTER the hide-planned filter and
    // independent of it: a removed task is not planned, so the two never conflict.
    if (removedOnly) {
      filtered = filtered.filter((task) => !!task.auto_route_excluded);
    }

    // Only what automation will never place — excludes `held`, which is blocked on
    // purpose and would otherwise pad the list an officer is meant to work through.
    if (blockedOnly && skip !== 'blocked') {
      filtered = filtered.filter((task) => task.routing_status?.state === 'blocked');
    }

    // Cold-chain only. Counted off cooling_count, the same number the snowflake
    // badge shows, so the filter and the badge can never disagree.
    if (coldOnly && skip !== 'cold') {
      filtered = filtered.filter((task) => (task.cooling_count ?? 0) > 0);
    }

    // Apply pickup-slot filter (S1..S5 / none / a SHIFT covering several slots)
    if (slotFilter === 'none') {
      filtered = filtered.filter((task) => !task.pickup_slot);
    } else if (slotsByShift[slotFilter]) {
      const members = slotsByShift[slotFilter];
      filtered = filtered.filter(
        (task) => !!task.pickup_slot?.slot_code && members.includes(task.pickup_slot.slot_code)
      );
    } else if (slotFilter !== 'all') {
      filtered = filtered.filter((task) => task.pickup_slot?.slot_code === slotFilter);
    }

    // Apply zone filter (multi-select)
    if (zoneFilter.length > 0) {
      filtered = filtered.filter((task) => {
        if (zoneFilter.includes('no_zone')) {
          return !task.zone_name || zoneFilter.includes(task.zone_name || '');
        }
        return task.zone_name && zoneFilter.includes(task.zone_name);
      });
    }
    
    // Apply search filter - search by merchant name, platform code, or UID only
    if (searchQuery) {
      // Normalised both sides. Core keeps merchant names with doubled internal
      // spaces (`عالمك··الافتراضي`) and HTML collapses them on screen, so typing
      // the name as displayed used to match nothing. See utils/search.ts.
      filtered = filtered.filter((task) =>
        matchesSearch(searchQuery, task.uid, task.platform_code, task.merchant_name)
      );
    }
    
    return filtered;
  }, [searchQuery, zoneFilter, hidePlanned, removedOnly, blockedOnly, coldOnly, dedicatedFilter, taskTypeFilter, slotFilter, slotsByShift]);

  const filteredTasks = useMemo(
    () => applyFilters(taskData?.tasks ?? []),
    [taskData?.tasks, applyFilters]
  );

  // Every badge and tab count is the list the page would show with only its own
  // filter removed, so each number matches the rows that appear when it is ticked.
  // Removed is the exception: counted off the unfiltered set, because it answers
  // "how many are held out today", not "how many are visible".
  const {
    plannedTaskCount,
    removedTaskCount,
    blockedTaskCount,
    dedicatedTaskCount,
    rtoTaskCount,
    pickupTaskCount,
    cashTaskCount,
  } = useMemo(() => {
    const tasks = taskData?.tasks ?? [];
    const countShown = (skip: FilterKey, pick: (t: RoutingTask) => boolean) =>
      applyFilters(tasks, skip).filter(pick).length;
    return {
      plannedTaskCount: countShown('planned', (t) => t.is_planned),
      removedTaskCount: tasks.filter((t) => !!t.auto_route_excluded).length,
      blockedTaskCount: countShown('blocked', (t) => t.routing_status?.state === 'blocked'),
      dedicatedTaskCount: countShown('dedicated', (t) => t.requires_dedicated_driver),
      rtoTaskCount: countShown('type', (t) => t.item_type === 'rto'),
      pickupTaskCount: countShown('type', (t) => t.item_type === 'pickup'),
      cashTaskCount: countShown('type', (t) => t.item_type === 'cash'),
    };
  }, [taskData?.tasks, applyFilters]);

  /**
   * Cold-chain sizing — both numbers, because the second is the one that
   * matters. A task is indivisible to the router, so flagging it pins EVERY
   * order on it to a refrigerated van: across 16 days of production, 274 cold
   * orders pulled 719 orders onto the cold fleet, a x2.6 multiplier. Orders
   * pinned, not cold orders, is what a two-van fleet actually has to carry, and
   * it is the number neither the PRD nor the consultant report tracks.
   */
  const coldTaskStats = useMemo(() => {
    const coldShown = applyFilters(taskData?.tasks ?? [], 'cold').filter((t) => (t.cooling_count ?? 0) > 0);
    return {
      tasks: coldShown.length,
      pinnedOrders: coldShown.reduce((sum, t) => sum + (t.number_of_orders ?? 0), 0),
    };
  }, [taskData?.tasks, applyFilters]);

  // Handle task click - don't allow selection of planned tasks
  const handleTaskClick = useCallback((task: RoutingTask) => {
    // Don't toggle selection for planned tasks
    if (task.is_planned) return;
    
    setSelectedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(task.uid)) {
        newSet.delete(task.uid);
      } else {
        newSet.add(task.uid);
      }
      return newSet;
    });
  }, []);

  // Navigate to route configuration page with selected tasks
  const handlePlanRoutes = useCallback(() => {
    // Use the first date if it's an array, otherwise use the single date
    const routeDate = Array.isArray(dateFilter) ? dateFilter[dateFilter.length - 1] : dateFilter;
    
    // Navigate to route config page with selected task UIDs
    navigate('/routes/configure', {
      state: {
        taskUids: Array.from(selectedTasks),
        routeDate,
      },
    });
  }, [selectedTasks, dateFilter, navigate]);

  // Handle mark task as done with auto-refresh
  const handleMarkTaskAsDone = useCallback(async (taskUid: string, merchantName?: string) => {
    const taskName = merchantName || taskUid.slice(-8);
    if (!confirm(`Are you sure you want to mark "${taskName}" as DONE?\n\nThis action will update the task status in the system.`)) {
      return;
    }

    setIsMarkingDone(taskUid);
    setErrorMessage(null);

    // Store current scroll position
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }

    try {
      await markTaskAsDone(taskUid);
      setSuccessMessage('Task marked as done successfully');

      // Auto-refresh task list
      await refetch();

      // Restore scroll position after refresh
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      }, 100);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to mark task as done:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to mark task as done');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setIsMarkingDone(null);
    }
  }, [refetch]);

  // Handle unassign (release task back to the pool) with auto-refresh
  const handleUnassignTask = useCallback(async (taskUid: string, merchantName?: string) => {
    const taskName = merchantName || taskUid.slice(-8);
    if (!confirm(
      `Unassign "${taskName}"?\n\n` +
      `This removes the task from its current route and driver and returns it to the pool so you can reassign it.\n\n` +
      `Note: if the driver has already started this route, their remaining stops will be re-optimized.`
    )) {
      return;
    }

    setIsUnassigning(taskUid);
    setErrorMessage(null);

    // Store current scroll position
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }

    try {
      await releaseTaskFromRoute(taskUid);
      setSuccessMessage('Task unassigned and returned to the pool');

      // Auto-refresh task list
      await refetch();

      // Restore scroll position after refresh
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      }, 100);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to unassign task:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to unassign task');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setIsUnassigning(null);
    }
  }, [refetch]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-gray-500">
              View and manage pickup and RTO tasks
            </p>
            {/* Polling indicator */}
            {(isPolling || isFetching) && (
              <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Refreshing...
              </span>
            )}
            {lastPolledAt && !isPolling && !isFetching && (
              <span className="text-xs text-gray-500">
                Last updated: {lastPolledAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad' })}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('split')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'split'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Split
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'map'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Map className="h-4 w-4" />
            </button>
          </div>

          {/* Plan routes button */}
          {can('routes.generate') && (
            <Button
              variant="primary"
              onClick={handlePlanRoutes}
              disabled={selectedTasks.size === 0}
              leftIcon={<Route className="h-4 w-4" />}
            >
              Plan Routes ({selectedTasks.size})
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-4">
        {/* Top Row: Search and Quick Actions */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-100">
          <div className="relative flex-1 max-w-lg">
            <input
              type="text"
              placeholder="Search by merchant name, platform code, or UID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full font-medium text-sm">
              {filteredTasks.length} tasks
            </span>
          </div>
        </div>

        {/* Filter Rows - Organized in sections */}
        <div className="p-4 space-y-3">
          {/* Primary Filters: Date and Status */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Filter - Today or Last Two Days */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Date</label>
              <div className="flex bg-white border border-gray-200 rounded-md p-0.5">
                <button
                  onClick={() => setDateMode('today')}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    dateMode === 'today'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setDateMode('last_two_days')}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    dateMode === 'last_two_days'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Last 2 Days
                </button>
                {/*
                  Carried-over work lives here. The filter runs on created_at, so
                  anything booked before today is invisible in the narrower modes
                  even while it is still open and still needs collecting.
                */}
                <button
                  onClick={() => setDateMode('last_7_days')}
                  title="Includes work carried over from previous days — a pickup booked last week that is still uncollected only appears here."
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    dateMode === 'last_7_days'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Last 7 Days
                </button>
                {/* Pick Date button hidden for now
                <button
                  onClick={() => setDateMode('specific_date')}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    dateMode === 'specific_date'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Pick Date
                </button>
                */}
              </div>
              {/* DatePicker hidden for now
              {dateMode === 'specific_date' && (
                <DatePickerWithCounts
                  value={specificDate}
                  onChange={(date) => setSpecificDate(date)}
                />
              )}
              */}
            </div>

            {/* Status Filter - Multi-select */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Status</label>
              <div className="relative" ref={statusDropdownRef}>
                <button
                  type="button"
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  className="min-w-[140px] px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white flex items-center justify-between gap-2 hover:border-gray-400 transition-colors"
                >
                  <span className={statusFilter.length === 0 ? 'text-gray-500' : 'text-gray-900 font-medium'}>
                    {statusFilter.length === 0 ? 'All Statuses' : `${statusFilter.length} selected`}
                  </span>
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {statusDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {['new', 'assigned', 'driving', 'arrived', 'in-progress', 'done', 'cancelled', 'stopped'].map((status) => (
                      <label key={status} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={statusFilter.includes(status)}
                          onChange={(e) => {
                            const newFilter = e.target.checked
                              ? [...statusFilter, status]
                              : statusFilter.filter(s => s !== status);
                            setStatusFilter(newFilter);
                            localStorage.setItem('tasksStatusFilter', JSON.stringify(newFilter));
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                        />
                        <span className="text-sm capitalize">{status}</span>
                      </label>
                    ))}
                    {statusFilter.length > 0 && (
                      <button
                        onClick={() => {
                          setStatusFilter([]);
                          localStorage.setItem('tasksStatusFilter', JSON.stringify([]));
                        }}
                        className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border-t border-gray-100 font-medium"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                )}
              </div>
              {statusFilter.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {statusFilter.slice(0, 2).map((status) => (
                    <span
                      key={status}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-md font-medium"
                    >
                      <span className="capitalize">{status}</span>
                      <button
                        onClick={() => {
                          const newFilter = statusFilter.filter(s => s !== status);
                          setStatusFilter(newFilter);
                          localStorage.setItem('tasksStatusFilter', JSON.stringify(newFilter));
                        }}
                        className="hover:text-blue-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {statusFilter.length > 2 && (
                    <span className="text-xs text-gray-500 font-medium">+{statusFilter.length - 2} more</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Secondary Filters: Type, Dedicated, Zones */}
          <div className="flex flex-wrap items-center gap-4">

            {/* Type Filter */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Type</label>
              <select
                value={taskTypeFilter}
                onChange={(e) => {
                  const value = e.target.value as 'all' | 'pickup' | 'rto' | 'cash';
                  setTaskTypeFilter(value);
                  localStorage.setItem('tasksTypeFilter', value);
                  // RTO/cash tasks never carry a pickup slot — an active S1..S5
                  // or SHIFT chip would silently empty the list, so drop it.
                  // NB "SHIFT1" does not match /^S\d/, so shift codes need the
                  // explicit membership test or they survive the switch.
                  const slotScoped = /^S\d/.test(slotFilter) || !!slotsByShift[slotFilter];
                  if (value !== 'all' && value !== 'pickup' && slotScoped) {
                    setSlotFilter('all');
                    localStorage.setItem('tasksSlotFilter', 'all');
                  }
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="all">All Tasks</option>
                <option value="pickup">Pickups ({pickupTaskCount})</option>
                <option value="rto">RTOs ({rtoTaskCount})</option>
                <option value="cash">Cash ({cashTaskCount})</option>
              </select>
            </div>

            {/* Pickup Slot Filter — S-chips only apply to pickups; RTO/cash tasks never book a slot */}
            <div className="flex items-center gap-1">
              {[
                'all',
                // Shift chips sit before the individual slots because a shift is
                // the wider net — an officer scoping "the evening wave" wants one
                // click, not three. Absent entirely when shift routing is off.
                ...shifts.map((s) => s.code),
                'S1', 'S2', 'S3', 'S4', 'S5',
                'none',
              ].map((code) => {
                const slotless = taskTypeFilter === 'rto' || taskTypeFilter === 'cash';
                const disabled = slotless && code !== 'all' && code !== 'none';
                const shift = shifts.find((s) => s.code === code);
                return (
                  <button
                    key={code}
                    disabled={disabled}
                    onClick={() => {
                      setSlotFilter(code);
                      localStorage.setItem('tasksSlotFilter', code);
                    }}
                    className={cn(
                      'rounded-full border px-2 py-0.5 text-xs',
                      disabled
                        ? 'cursor-not-allowed border-gray-100 text-gray-300'
                        : slotFilter === code
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : shift
                            // Shifts read as a group, not another slot — a heavier
                            // chip keeps them from looking like S6, S7.
                            ? 'border-indigo-200 bg-indigo-50/60 font-medium text-indigo-700 hover:bg-indigo-100'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    )}
                    title={
                      disabled
                        ? `${taskTypeFilter === 'rto' ? 'RTO' : 'Cash'} tasks don't book pickup slots`
                        : shift
                          ? `${shift.code}: ${shift.slot_codes.join(' + ')} (${shift.window.from}–${shift.window.to})`
                          : code === 'none'
                            ? 'Tasks without a platform slot'
                            : code === 'all'
                              ? 'All slots'
                              : `Slot ${code}`
                    }
                  >
                    {code === 'all' ? 'All slots' : code === 'none' ? 'No slot' : code}
                  </button>
                );
              })}
            </div>

            {/* Dedicated Filter */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Dedicated</label>
              <select
                value={dedicatedFilter}
                onChange={(e) => {
                  const value = e.target.value as 'all' | 'dedicated' | 'non_dedicated';
                  setDedicatedFilter(value);
                  localStorage.setItem('tasksDedicatedFilter', value);
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="all">All</option>
                <option value="dedicated">Dedicated ({dedicatedTaskCount})</option>
                <option value="non_dedicated">Non-Dedicated</option>
              </select>
            </div>

            {/* Zone Filter */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Zones</label>
              <div className="relative" ref={zoneDropdownRef}>
                <button
                  type="button"
                  onClick={() => setZoneDropdownOpen(!zoneDropdownOpen)}
                  className="min-w-[140px] px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white flex items-center justify-between gap-2 hover:border-gray-400 transition-colors"
                >
                  <span className={zoneFilter.length === 0 ? 'text-gray-500' : 'text-gray-900 font-medium'}>
                    {zoneFilter.length === 0 ? 'All Zones' : `${zoneFilter.length} selected`}
                  </span>
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {zoneDropdownOpen && (
                <div className="absolute z-50 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                    <input
                      type="checkbox"
                      checked={zoneFilter.includes('no_zone')}
                      onChange={(e) => {
                        const newFilter = e.target.checked
                          ? [...zoneFilter, 'no_zone']
                          : zoneFilter.filter(z => z !== 'no_zone');
                        setZoneFilter(newFilter);
                        localStorage.setItem('tasksZoneFilter', JSON.stringify(newFilter));
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-sm text-red-600 font-medium">No Zone</span>
                  </label>
                  {availableZones.map((zone) => (
                    <label key={zone} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={zoneFilter.includes(zone)}
                        onChange={(e) => {
                          const newFilter = e.target.checked
                            ? [...zoneFilter, zone]
                            : zoneFilter.filter(z => z !== zone);
                          setZoneFilter(newFilter);
                          localStorage.setItem('tasksZoneFilter', JSON.stringify(newFilter));
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm">{zone}</span>
                    </label>
                  ))}
                  {zoneFilter.length > 0 && (
                    <button
                      onClick={() => {
                        setZoneFilter([]);
                        localStorage.setItem('tasksZoneFilter', JSON.stringify([]));
                      }}
                      className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border-t border-gray-100 font-medium"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              )}
            </div>
            {zoneFilter.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {zoneFilter.slice(0, 2).map((zone) => (
                  <span
                    key={zone}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                  >
                    {zone === 'no_zone' ? 'No Zone' : zone}
                    <button
                      onClick={() => {
                        const newFilter = zoneFilter.filter(z => z !== zone);
                        setZoneFilter(newFilter);
                        localStorage.setItem('tasksZoneFilter', JSON.stringify(newFilter));
                      }}
                      className="hover:text-blue-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {zoneFilter.length > 2 && (
                  <span className="text-xs text-gray-500">+{zoneFilter.length - 2} more</span>
                )}
              </div>
            )}
            </div>

            {/* Hide Planned Toggle */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hidePlanned}
                  onChange={(e) => {
                    setHidePlanned(e.target.checked);
                    localStorage.setItem('tasksHidePlanned', JSON.stringify(e.target.checked));
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 font-medium">
                  Hide Planned
                  {plannedTaskCount > 0 && (
                    <span className="ml-1 text-amber-600">({plannedTaskCount})</span>
                  )}
                </span>
              </label>
            </div>

            {/*
              Only offered when there is something to find. A badge answers "is
              this one held out"; this answers "show me all of them", which is
              the actual question when a shift's work is short and nobody
              remembers what was pulled.
            */}
            {(blockedTaskCount > 0 || blockedOnly) && (
              <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={blockedOnly}
                    onChange={(e) => {
                      setBlockedOnly(e.target.checked);
                      localStorage.setItem('tasksBlockedOnly', JSON.stringify(e.target.checked));
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span
                    className="text-sm font-medium text-gray-700"
                    title="Tasks automation will never place — rolled over, no zone, no route, or full. Each row says which. These need a person."
                  >
                    Not routable only
                    <span className="ml-1 text-red-600">({blockedTaskCount})</span>
                  </span>
                </label>
              </div>
            )}

            {(coldTaskStats.tasks > 0 || coldOnly) && (
              <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={coldOnly}
                    onChange={(e) => {
                      setColdOnly(e.target.checked);
                      localStorage.setItem('tasksColdOnly', JSON.stringify(e.target.checked));
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span
                    className="text-sm font-medium text-gray-700"
                    title={`${coldTaskStats.tasks} task(s) carry temperature-controlled orders, pinning ${coldTaskStats.pinnedOrders} order(s) in total to a refrigerated van — a task cannot be split, so every order on it goes along.`}
                  >
                    Cold chain only
                    <span className="ml-1 text-sky-600">
                      ({coldTaskStats.tasks} · {coldTaskStats.pinnedOrders} orders)
                    </span>
                  </span>
                </label>
              </div>
            )}

            {(removedTaskCount > 0 || removedOnly) && (
              <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={removedOnly}
                    onChange={(e) => {
                      setRemovedOnly(e.target.checked);
                      localStorage.setItem('tasksRemovedOnly', JSON.stringify(e.target.checked));
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                  />
                  <span
                    className="text-sm font-medium text-gray-700"
                    title="Tasks an officer took off a route today — auto-routing will not pick them up again until someone adds them back by hand"
                  >
                    Removed only
                    <span className="ml-1 text-rose-600">({removedTaskCount})</span>
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-800">Failed to load tasks</p>
            <p className="text-sm text-red-600">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="ml-auto"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`grid gap-4 ${
          viewMode === 'split' ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
        }`}
        style={{ height: 'calc(100vh - 200px)', minHeight: '400px' }}
      >
        {/* Task Table */}
        {(viewMode === 'list' || viewMode === 'split') && (
          <div className="overflow-auto h-full" ref={scrollContainerRef}>
            {/* Loading overlay during refresh */}
            {isFetching && !isLoading && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10" role="status">
                <div className="bg-white rounded-lg shadow-lg p-4 flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-gray-700">Refreshing tasks...</span>
                </div>
              </div>
            )}
            <TaskTable
              tasks={filteredTasks}
              selectedTasks={selectedTasks}
              onSelectionChange={setSelectedTasks}
              onTaskClick={handleTaskClick}
              isLoading={isLoading}
              onMarkTaskAsDone={handleMarkTaskAsDone}
              isMarkingDone={isMarkingDone}
              onUnassignTask={handleUnassignTask}
              isUnassigning={isUnassigning}
            />
          </div>
        )}

        {/* Task Map */}
        {(viewMode === 'map' || viewMode === 'split') && (
          <div className="h-full w-full" style={{ height: '100%', width: '100%' }}>
            <TaskMap
              tasks={filteredTasks}
              selectedTasks={selectedTasks}
              onTaskSelect={(taskUid) => {
                const task = filteredTasks.find(t => t.uid === taskUid);
                if (task) handleTaskClick(task);
              }}
              className="h-full w-full"
            />
          </div>
        )}
      </div>
    </div>
  );
}
